import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type ActivityStats, type RecentCompletion } from '@/api';

const DATE_RANGES = [
  { value: '1h', label: 'Last 1 hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
] as const;

type DateRangeValue = (typeof DATE_RANGES)[number]['value'];

function getFromDate(range: DateRangeValue): string | undefined {
  if (range === 'all') return undefined;
  const now = Date.now();
  const ms =
    range === '1h' ? 60 * 60 * 1000
    : range === '24h' ? 24 * 60 * 60 * 1000
    : range === '7d' ? 7 * 24 * 60 * 60 * 1000
    : range === '30d' ? 30 * 24 * 60 * 60 * 1000
    : 0;
  return new Date(now - ms).toISOString();
}

const cards = [
  { to: '/content', label: 'Content', description: 'AI design, import & export JSON' },
  { to: '/pathways', label: 'Pathways', description: 'Manage learning pathways' },
  { to: '/tracks', label: 'Tracks', description: 'Manage learning tracks' },
  { to: '/lessons', label: 'Lessons', description: 'Manage lessons and questions' },
  { to: '/achievements', label: 'Achievements', description: 'Manage achievements' },
];

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

export function Dashboard() {
  const [dateRange, setDateRange] = useState<DateRangeValue>('24h');
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [completions, setCompletions] = useState<RecentCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    const from = getFromDate(dateRange);
    Promise.all([
      adminApi.activity.stats(from),
      adminApi.activity.recentCompletions(20, from),
    ])
      .then(([s, c]) => {
        if (mounted) {
          setStats(s);
          setCompletions(c);
        }
      })
      .catch((e) => {
        if (mounted) setError(e instanceof Error ? e.message : 'Failed to load activity');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [dateRange]);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-800 mb-6">Dashboard</h1>

      {/* Date range filter */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-600">Filter:</span>
        <div className="flex flex-wrap gap-1">
          {DATE_RANGES.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setDateRange(value)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                dateRange === value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Activity stats */}
      <section className="mb-8">
        <h2 className="text-lg font-medium text-slate-700 mb-4">User activity</h2>
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-2/3 mb-2" />
                <div className="h-8 bg-slate-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">
            {error}
          </div>
        ) : stats ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-500">Total users</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.totalUsers.toLocaleString()}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-500">Active users</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.activeUsersLast7Days.toLocaleString()}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-500">Lesson completions (total)</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.totalLessonCompletions.toLocaleString()}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-500">Completions in range</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.completionsLast7Days.toLocaleString()}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-500">PRO subscribers</p>
              <p className="text-2xl font-semibold text-indigo-600">{stats.proSubscribers.toLocaleString()}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-500">Achievements unlocked</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.achievementsUnlocked.toLocaleString()}</p>
            </div>
          </div>
        ) : null}
      </section>

      {/* Recent completions */}
      <section className="mb-10">
        <h2 className="text-lg font-medium text-slate-700 mb-4">Recent lesson completions</h2>
        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="divide-y divide-slate-100">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="px-4 py-3 animate-pulse flex gap-4">
                  <div className="h-4 bg-slate-200 rounded flex-1" />
                  <div className="h-4 bg-slate-100 rounded w-24" />
                </div>
              ))}
            </div>
          </div>
        ) : error ? null : completions.length > 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Lesson</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Track</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">XP</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {completions.map((c, i) => (
                    <tr key={i} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 text-sm text-slate-800">
                        {c.userDisplayName && c.userEmail
                          ? `${c.userDisplayName} (${c.userEmail})`
                          : c.userDisplayName
                            ? c.userDisplayName
                            : c.userEmail
                              ? `User (${c.userEmail})`
                              : (
                                  <span className="font-mono text-slate-500">{c.userId.slice(0, 12)}…</span>
                                )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">{c.lessonTitle}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{c.trackName}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">+{c.xpEarned}</td>
                      <td className="px-4 py-3 text-sm text-slate-500 text-right">{formatRelativeTime(c.completedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-slate-500 text-sm">
            No lesson completions yet.
          </div>
        )}
      </section>

      {/* Content shortcuts */}
      <section>
        <h2 className="text-lg font-medium text-slate-700 mb-4">Content</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {cards.map(({ to, label, description }) => (
            <Link
              key={to}
              to={to}
              className="block rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <h2 className="font-semibold text-slate-800">{label}</h2>
              <p className="mt-1 text-sm text-slate-600">{description}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
